import type {
  AudienceCommentsPayload,
  Competitor,
  InsightKind,
  MarketFormatsPayload,
  OwnVsMarket,
  SegmentRow,
  SentimentMood,
  SentimentTotals,
} from '../types';

/** Pure helpers behind the Market v2 charts; no React so they are easy to test. */

export type MarketView =
  | 'pulse'
  | 'trending'
  | 'competitors'
  | 'formats'
  | 'money';

export const MARKET_VIEWS: Array<{ key: MarketView; label: string }> = [
  { key: 'pulse', label: 'Pulse' },
  { key: 'trending', label: 'Trending now' },
  { key: 'competitors', label: 'Competitors' },
  { key: 'formats', label: 'Formats & topics' },
  { key: 'money', label: 'Money' },
];

export const MARKET_VIEW_KINDS: Record<MarketView, InsightKind[]> = {
  pulse: ['market_formats', 'competitors'],
  trending: ['market_trending'],
  competitors: ['competitors', 'business_competitors'],
  formats: ['market_formats', 'market_topics'],
  money: ['commercial'],
};

export const VIEW_PARAM = 'view';

export const parseView = (v: string | null | undefined): MarketView =>
  MARKET_VIEWS.some((x) => x.key === v) ? (v as MarketView) : 'pulse';

/** Change in share of uploads, late vs early window, in percentage points. */
export const shareDeltaPp = (r: SegmentRow) =>
  (r.shareLate - r.shareEarly) * 100;

export type Quadrant = 'growing' | 'underused' | 'crowding' | 'fading';

export const QUADRANT_LABEL: Record<Quadrant, string> = {
  growing: 'Growing & working',
  underused: 'Underused winners',
  crowding: 'Crowding',
  fading: 'Fading',
};

/** x = share change (pp), y = median views vs creator norm (1× = typical). */
export const quadrantOf = (dxPp: number, y: number): Quadrant => {
  const up = dxPp >= 0;
  const works = y >= 1;
  if (up) return works ? 'growing' : 'crowding';
  return works ? 'underused' : 'fading';
};

/**
 * Bubble radius ∝ √videos. The biggest format (recipe tutorials: half the
 * market) would shrink every other bubble to a dot, so the scale caps at the
 * second-largest count and anything above it gets the max radius.
 */
export const bubbleRadius = (
  videos: number,
  all: number[],
  minR = 5,
  maxR = 24,
) => {
  const sorted = [...all].sort((a, b) => b - a);
  const cap = Math.max(1, sorted[1] ?? sorted[0] ?? 1);
  const t = Math.sqrt(Math.min(videos, cap) / cap);
  return minR + (maxR - minR) * t;
};

/** Heat-strip cell: below 10 videos the median is noise and the cell is grey. */
export const HEAT_MIN_N = 10;

export const heatCell = (n: number, median: number | null | undefined) => {
  if (median == null || n < HEAT_MIN_N)
    return { grey: true as const, tone: 'even' as const, strength: 0 };
  const log = Math.log2(Math.max(median, 0.01));
  // 2× (or ½×) is full colour; ±5% reads as even.
  const strength = Math.min(1, Math.abs(log));
  const tone: 'above' | 'below' | 'even' =
    Math.abs(median - 1) < 0.05 ? 'even' : median > 1 ? 'above' : 'below';
  return { grey: false as const, tone, strength };
};

const MOVER_MIN_VIDEOS = 50;

/** Formats whose share of uploads moved the most; tiny and catch-all segments skipped. */
export const rankMovers = (rows: SegmentRow[], k = 4) =>
  rows
    .filter(
      (r) =>
        r.key !== 'other' &&
        r.videos >= MOVER_MIN_VIDEOS &&
        r.medianLate != null,
    )
    .sort((a, b) => Math.abs(shareDeltaPp(b)) - Math.abs(shareDeltaPp(a)))
    .slice(0, k);

/** The format working best right now, among ones with enough videos to trust. */
export const hottestFormat = (rows: SegmentRow[]) =>
  rows
    .filter((r) => r.key !== 'other' && r.videos >= 100 && r.medianLate != null)
    .sort((a, b) => (b.medianLate || 0) - (a.medianLate || 0))[0];

/** Your formats with enough videos to outline on the map. */
export const ownKeys = (mhd: OwnVsMarket[], min = 3) =>
  new Set(mhd.filter((r) => r.videos >= min).map((r) => r.key));

/** Your median vs the market's in the same formats, weighted by how often you post each. */
export const youVsMarket = (mhd: OwnVsMarket[]) => {
  const rows = mhd.filter((r) => r.marketMedian != null && r.videos > 0);
  const n = rows.reduce((s, r) => s + r.videos, 0);
  if (!n) return null;
  const you = rows.reduce((s, r) => s + r.mhdMedian * r.videos, 0) / n;
  const market =
    rows.reduce((s, r) => s + (r.marketMedian as number) * r.videos, 0) / n;
  return { you, market, ratio: market ? you / market : null };
};

const lower = (s: string) => s.charAt(0).toLowerCase() + s.slice(1);

const fmtShare = (v: number) =>
  v < 0.1 ? `${(v * 100).toFixed(1)}%` : `${Math.round(v * 100)}%`;

/**
 * Your most over-represented format vs the market, only where both shares
 * mean something: market ≥ 1% of uploads, yours ≥ 5%, and a 1.5–10× gap
 * (a format at 0.2% of the market makes any ratio absurd).
 */
export const mixContrast = (p: MarketFormatsPayload) => {
  const ownTotal = p.mhd.reduce((s, r) => s + r.videos, 0);
  if (ownTotal < 10) return null;
  return (
    p.mhd
      .map((r) => {
        const market = p.all.find((x) => x.key === r.key)?.shareLate || 0;
        const own = r.videos / ownTotal;
        return {
          key: r.key,
          label: r.label,
          own,
          market,
          ratio: market ? own / market : 0,
        };
      })
      .filter(
        (x) =>
          x.market >= 0.01 && x.own >= 0.05 && x.ratio >= 1.5 && x.ratio <= 10,
      )
      .sort((a, b) => b.ratio - a.ratio)[0] || null
  );
};

/** One sentence about the market when the snapshot has no grounded headline. */
export const templateHeadline = (
  p: MarketFormatsPayload,
  length: 'shorts' | 'long',
) => {
  const rows = p[length].filter(
    (r) => r.key !== 'other' && r.videos >= MOVER_MIN_VIDEOS,
  );
  if (!rows.length) return null;
  const hot = hottestFormat(rows);
  const loser = [...rows].sort((a, b) => shareDeltaPp(a) - shareDeltaPp(b))[0];
  const parts: string[] = [];
  if (hot)
    parts.push(
      (hot.medianLate || 0) > 1
        ? `${hot.label} beats its creators' norms (${(
            hot.medianLate || 0
          ).toFixed(1)}×)`
        : `${hot.label} is the highest-ranked format (${(
            hot.medianLate || 0
          ).toFixed(2)}× norm)`,
    );
  if (loser && shareDeltaPp(loser) < -0.5 && loser.key !== hot?.key)
    parts.push(
      `${lower(loser.label)} loses share (−${Math.abs(
        shareDeltaPp(loser),
      ).toFixed(1)} pp)`,
    );
  let sentence = parts.length ? `${parts.join(' while ')}.` : '';
  const mix = mixContrast(p);
  if (mix)
    sentence += ` ${mix.label} is ${Math.round(
      mix.own * 100,
    )}% of your uploads vs ${fmtShare(mix.market)} for the market.`;
  return sentence.trim() || null;
};

export const MOODS: Array<{
  key: SentimentMood;
  label: string;
  color: string;
}> = [
  { key: 'positive', label: 'Loved', color: '#1baf7a' },
  { key: 'neutral', label: 'Neutral', color: '#a3a29d' },
  { key: 'asked', label: 'Asked', color: '#2a78d6' },
  { key: 'negative', label: 'Complained', color: '#e34948' },
];

/** Comment kinds → mood (praise → loved, question/request → asked, other → neutral). */
export const moodFromCounts = (
  counts: AudienceCommentsPayload['counts'],
): SentimentTotals => ({
  positive: counts.praise || 0,
  neutral: counts.other || 0,
  asked: (counts.question || 0) + (counts.request || 0),
  negative: counts.negative || 0,
});

export const moodShares = (t: SentimentTotals) => {
  const total = MOODS.reduce((s, m) => s + (t[m.key] || 0), 0);
  return MOODS.map((m) => ({
    ...m,
    count: t[m.key] || 0,
    share: total ? (t[m.key] || 0) / total : 0,
  }));
};

/** Share change in percentage points vs the previous 90 days; null without a baseline. */
export const moodDeltaPp = (
  now: SentimentTotals,
  prev?: SentimentTotals | null,
) => {
  if (!prev) return null;
  const a = moodShares(now);
  const b = moodShares(prev);
  if (!b.some((x) => x.count)) return null;
  return Object.fromEntries(
    a.map((x, i) => [x.key, (x.share - b[i].share) * 100]),
  ) as Record<SentimentMood, number>;
};

export const polarityLabel = (p: number) =>
  p >= 1 ? 'Positive' : p <= -1 ? 'Negative' : 'Neutral';

/** Theme trend points below this many comments that month are too thin to read. */
export const THEME_MIN_N = 100;

export type Freshness = 'fresh' | 'stale' | 'expired';

/** Trending ages fast: amber after 7 days, hidden after 21. */
export const freshnessOf = (at: string | undefined, now = Date.now()) => {
  const t = at ? Date.parse(at) : NaN;
  if (Number.isNaN(t)) return { days: null, state: 'expired' as Freshness };
  const days = Math.max(0, Math.floor((now - t) / 86_400_000));
  const state: Freshness = days > 21 ? 'expired' : days > 7 ? 'stale' : 'fresh';
  return { days, state };
};

export const agoLabel = (days: number | null) =>
  days == null
    ? 'unknown'
    : days === 0
    ? 'today'
    : days === 1
    ? 'yesterday'
    : `${days} days ago`;

export const TIERS: Array<{ key: string; label: string; info: string }> = [
  {
    key: 'direct',
    label: 'Direct',
    info: 'Same audience and a similar show: people who comment on you comment on them too, and they post the same kind of videos.',
  },
  {
    key: 'audience',
    label: 'Audience rivals',
    info: 'Different kind of channel, but a big share of their commenters also comment on you.',
  },
  {
    key: 'format',
    label: 'Format peers',
    info: 'Channels of a similar type and size that post the same formats; little shared audience so far.',
  },
];

/** Competitors in tier order, best score first inside a tier; unknown tiers last. */
export const groupCompetitors = (list: Competitor[]) => {
  const known = TIERS.map((t) => t.key);
  const groups = TIERS.map((t) => ({
    ...t,
    items: list
      .filter((c) => c.tier === t.key)
      .sort((a, b) => b.score - a.score),
  }));
  const other = list.filter((c) => !known.includes(c.tier));
  if (other.length)
    groups.push({
      key: 'other',
      label: 'Other',
      info: '',
      items: other.sort((a, b) => b.score - a.score),
    });
  return groups.filter((g) => g.items.length);
};

/** The closest rival: top direct competitor, else the best-scored one. */
export const topRival = (list: Competitor[]) =>
  groupCompetitors(list)[0]?.items[0];

/**
 * Views vs the creator's own baseline, for badges. A tiny baseline makes
 * 1,690× possible; past 50× the exact number means nothing, so it is capped.
 */
export const OUTLIER_CAP = 50;
export const fmtOutlier = (v?: number | null) =>
  v == null || Number.isNaN(v)
    ? undefined
    : v > OUTLIER_CAP
    ? `>${OUTLIER_CAP}×`
    : `${v.toFixed(1)}×`;

/**
 * Thumbnails for a segment card: the strongest believable breakouts first.
 * Evidence is sorted by outlier, and the top of a big segment is often
 * hundreds of × from a tiny baseline, which would make every badge ">50×".
 */
export const pickThumbs = <
  T extends { videoId?: string; outlier?: number | null },
>(
  items: T[],
  n = 3,
) => {
  const withId = items.filter((e) => e.videoId);
  const sane = withId.filter(
    (e) => e.outlier != null && e.outlier <= OUTLIER_CAP,
  );
  const rest = withId.filter((e) => !sane.includes(e));
  return [...sane, ...rest].slice(0, n);
};

export const ytThumb = (videoId: string) =>
  `https://i.ytimg.com/vi/${videoId}/mqdefault.jpg`;

export const ytWatch = (videoId: string) =>
  `https://www.youtube.com/watch?v=${videoId}`;

export const daysSince = (iso: string, now = Date.now()) =>
  Math.max(0, Math.floor((now - Date.parse(iso)) / 86_400_000));

export const shortDate = (iso?: string | null) =>
  iso
    ? new Date(iso).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      })
    : '';
